'use client';
import Accordion from '@ui/Accordion';
import { cn } from '@util/index';
import Link from 'next/link';
import { useState } from 'react';

export type FaqAccordionItem = {
  q: string;
  a: React.ReactNode;
};

interface FaqAccordionProps {
  questions: FaqAccordionItem[];
  initialValue?: string;
  title?: string;
  titleClassName?: string;
  questionClassName?: string;
  textClassName?: string;
  className?: string;
}

export const FaqAccordion = ({
  questions,
  initialValue = questions[0]?.q,
  title = 'Frequently Asked Questions',
  titleClassName,
  questionClassName,
  textClassName,
  className,
}: FaqAccordionProps) => {
  const [currentTopic, setCurrentTopic] = useState<string | undefined>(
    initialValue
  );

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-x-24 lg:flex-row lg:items-start',
        className
      )}
    >
      <div className="flex flex-col items-center gap-4 lg:items-start">
        <h3
          className={cn(
            'w-[30rem] text-center text-[3.4rem] font-semibold lg:text-left',
            titleClassName
          )}
        >
          {title}
        </h3>

        <h3 className="text-center text-[1.8rem] lg:text-left">
          Still have questions? Feel free to{' '}
          <Link href="/contact" className="font-semibold text-brand-secondary">
            Contact Us
          </Link>
          .
        </h3>
      </div>

      <div className="flex w-full flex-col gap-[6rem] px-16 pt-16 sm:mt-0 lg:mx-auto lg:w-[90rem] lg:max-w-default lg:px-0 lg:pt-[13rem]">
        <div className="flex flex-col gap-[3.2rem]">
          {questions.map((faq, i) => (
            <Accordion
              key={`${faq.q}${i}`}
              title={faq.q}
              divider={true}
              hover="text"
              intent="faq"
              highlightHeaderOnOpen={true}
              arrowRight
              mobileArrowRotation
              open={currentTopic === faq.q}
              onOpen={() => setCurrentTopic(faq.q)}
              onClose={() => setCurrentTopic(undefined)}
              titleClassName={questionClassName}
            >
              <div className={cn('pl-[1.6rem]', textClassName)}>{faq.a}</div>
            </Accordion>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaqAccordion;
